import React from "react";

/**
 * GhostPhone — semi-transparent silhouette of a phone used as a background
 * "team member" indicator behind the main phones in the workflow section.
 * Each ghost drifts independently via the .ghost-1/-2/-3 animation classes.
 */
export const GhostPhone = ({
  variant = 1,
  side = "left", // "left" | "right" — which main phone it sits behind
  scale = 0.7,
  opacity = 0.18,
  offsetX = 0,
  offsetY = 0,
}) => {
  const animClass =
    variant === 1 ? "ghost-1" : variant === 2 ? "ghost-2" : "ghost-3";

  return (
    <div
      aria-hidden="true"
      data-testid={`ghost-phone-${side}-${variant}`}
      className={`absolute pointer-events-none ${animClass}`}
      style={{
        top: "50%",
        left: "50%",
        width: 290 * scale,
        height: 600 * scale,
        marginLeft: -(290 * scale) / 2 + offsetX,
        marginTop: -(600 * scale) / 2 + offsetY,
        opacity,
      }}
    >
      {/* Outer case silhouette */}
      <div
        className="relative w-full h-full bg-[#1a1a1a] border-2 border-[#FFEA00]/30"
        style={{
          borderRadius: 38 * scale,
          boxShadow:
            "0 0 60px rgba(255,234,0,0.18), 0 20px 40px -10px rgba(0,0,0,0.6)",
        }}
      >
        {/* Bezel/screen area */}
        <div
          className="absolute inset-[10px] bg-black overflow-hidden"
          style={{ borderRadius: 28 * scale }}
        >
          {/* Notch hint */}
          <div
            className="absolute top-2 left-1/2 -translate-x-1/2 bg-black rounded-full"
            style={{ width: 60 * scale, height: 12 * scale }}
          />
          {/* Faint screen content suggestion */}
          <div
            className="absolute inset-x-4 top-10 h-2 bg-[#FFEA00]/40"
            style={{ borderRadius: 1 }}
          />
          <div className="absolute inset-x-4 top-16 h-1.5 bg-white/20" />
          <div className="absolute inset-x-4 top-24 h-1.5 bg-white/15" />
          <div className="absolute inset-x-6 bottom-10 h-px bg-[#FFEA00]/30" />
          {/* Bottom nav dot row */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-around">
            <span className="w-1 h-1 bg-[#FFEA00]/50" />
            <span className="w-1 h-1 bg-white/20" />
            <span className="w-1 h-1 bg-white/20" />
            <span className="w-1 h-1 bg-white/20" />
          </div>
        </div>

        {/* Neon corner accent */}
        <div className="absolute -top-px -right-px w-1.5 h-1.5 bg-[#FFEA00]/60" />
      </div>
    </div>
  );
};

/** Cluster of 3 ghost phones behind a main phone — depth + team feel. */
export const GhostCluster = ({ side = "left" }) => {
  // Different offsets/scales per side so each cluster has its own personality
  const isLeft = side === "left";
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
      data-testid={`ghost-cluster-${side}`}
    >
      <GhostPhone
        variant={1}
        side={side}
        scale={0.78}
        opacity={0.22}
        offsetX={isLeft ? -90 : 90}
        offsetY={-10}
      />
      <GhostPhone
        variant={2}
        side={side}
        scale={0.68}
        opacity={0.16}
        offsetX={isLeft ? 70 : -70}
        offsetY={30}
      />
      <GhostPhone
        variant={3}
        side={side}
        scale={0.6}
        opacity={0.11}
        offsetX={isLeft ? -50 : 50}
        offsetY={60}
      />
    </div>
  );
};
